import { Euler, Quaternion, Vector3 } from 'three';

/**
 * The rig layout of the robot, in the body's local frame.
 *
 * These constants are shared by the view (`RobotView`) and the aiming code
 * (`CameraRig`), so the gun the player can see and the gun that fires are
 * always the same gun.
 */

/**
 * The head pivot's position in the body's local frame.
 *
 * The mount is dorsal: it rises from the top of the body and holds the camera
 * pod and the AR gun above the chassis, not in front of a dog-like face.
 */
export const HEAD_LOCAL = new Vector3(0, 0.52, -0.28);

/** Where the Z-mount leaves the top of the body. */
export const NECK_BASE_LOCAL = new Vector3(0, 0.22, 0.14);

/** The first knuckle, at the top of the riser. */
export const NECK_ELBOW_1_LOCAL = new Vector3(0, 0.46, 0.1);

/** The second knuckle, at the front of the boom. */
export const NECK_ELBOW_2_LOCAL = new Vector3(0, 0.66, -0.22);

/** Number of rigid links in the Z-mount. */
export const NECK_LINKS = 3;

/** The camera pod's lens centre, which is also the first-person eye. */
export const CAMERA_LENS_LOCAL = new Vector3(0, 0.12, -0.14);

/** The AR receiver's centre in the head's local frame, centred left/right. */
export const GUN_LOCAL = new Vector3(0, -0.06, -0.68);

/** The muzzle's position in the head's local frame. */
export const MUZZLE_LOCAL = new Vector3(0, -0.06, -1.55);

const scratchNeckBase = new Vector3();
const scratchNeckElbow1 = new Vector3();
const scratchNeckElbow2 = new Vector3();
const scratchNeckTip = new Vector3();
const scratchNeckShift = new Vector3();

/**
 * Sample the four joints of the dorsal Z-mount: base, elbow 1, elbow 2, head.
 *
 * The profile is deliberately a Z in side view: a vertical riser, a forward
 * boom, then a short drop to the head. The tip stays at `HEAD_LOCAL` so the
 * camera and gun contract is stable; the elbows flex towards the aim so the
 * mount reads as articulated without moving the eye.
 */
export function neckJoints(
  yaw: number,
  pitch: number,
  out: Vector3[],
  /** Body-local translation of the head rig, so the mount follows the pod. */
  offset?: Vector3,
): void {
  const clampYaw = Math.max(-1.2, Math.min(1.2, yaw));
  const clampPitch = Math.max(-1.2, Math.min(1.2, pitch));

  scratchNeckBase.copy(NECK_BASE_LOCAL);
  scratchNeckElbow1.copy(NECK_ELBOW_1_LOCAL);
  scratchNeckElbow2.copy(NECK_ELBOW_2_LOCAL);
  scratchNeckTip.copy(HEAD_LOCAL);

  // The two knuckles lean into the aim; the tip stays on the camera contract.
  scratchNeckElbow1.x += clampYaw * 0.05;
  scratchNeckElbow1.y += clampPitch * 0.04;
  scratchNeckElbow2.x += clampYaw * 0.12;
  scratchNeckElbow2.y += clampPitch * 0.09;
  // Match the visual neck stretch and the first-person eye, which both lift
  // the head slightly as the pitch grows.
  scratchNeckTip.y += Math.abs(pitch) * 0.06;

  if (offset) {
    scratchNeckShift.copy(offset).multiplyScalar(1 / 3);
    scratchNeckElbow1.add(scratchNeckShift);
    scratchNeckShift.copy(offset).multiplyScalar(2 / 3);
    scratchNeckElbow2.add(scratchNeckShift);
    scratchNeckTip.add(offset);
  }

  out[0] = (out[0] ?? new Vector3()).copy(scratchNeckBase);
  out[1] = (out[1] ?? new Vector3()).copy(scratchNeckElbow1);
  out[2] = (out[2] ?? new Vector3()).copy(scratchNeckElbow2);
  out[3] = (out[3] ?? new Vector3()).copy(scratchNeckTip);
}

const scratchEuler = new Euler(0, 0, 0, 'YXZ');

/**
 * The head's local rotation for an aim offset, in radians.
 *
 * The game's aim convention is positive yaw = turn right (see
 * `rightFromForwardUp`), but three.js's positive Y rotation turns the local
 * forward `(0, 0, -1)` towards -X, which is the *left*. The yaw is negated
 * here so the head mesh, the gun and the firing muzzle all agree with the
 * aim. Every consumer goes through this function for that reason.
 */
export function headRotation(
  yaw: number,
  pitch: number,
  roll: number,
  out = new Quaternion(),
): Quaternion {
  scratchEuler.set(pitch, -yaw, roll, 'YXZ');
  return out.setFromEuler(scratchEuler);
}

/**
 * The muzzle's world position for a body transform and a head rotation.
 *
 * The head's local rotation is applied first, then the head is placed on the
 * body, and finally the body is placed in the world.
 */
export function muzzleWorldPosition(
  bodyPosition: Vector3,
  bodyQuaternion: Quaternion,
  headQuaternion: Quaternion,
  out = new Vector3(),
  /** Body-local translation of the head rig, in metres. */
  offset?: Vector3,
): Vector3 {
  out.copy(MUZZLE_LOCAL).applyQuaternion(headQuaternion);
  out.add(HEAD_LOCAL);
  if (offset) out.add(offset);
  out.applyQuaternion(bodyQuaternion);
  return out.add(bodyPosition);
}

/** The head pivot's world position for a body transform. */
export function headWorldPosition(
  bodyPosition: Vector3,
  bodyQuaternion: Quaternion,
  out = new Vector3(),
  /** Body-local translation of the head rig, in metres. */
  offset?: Vector3,
): Vector3 {
  out.copy(HEAD_LOCAL);
  if (offset) out.add(offset);
  out.applyQuaternion(bodyQuaternion);
  return out.add(bodyPosition);
}
